import React, { Fragment } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { SPACING } from 'app-v2/design-tokens'
import { colors } from 'shared/assets/styles'
import { ecommOrderToCart } from 'shared/store/ecommerce/catalog'
import type { EcommOrder } from 'shared/store/ecommerce/order'
import type {
    CacOrderContent,
    CacOrderContentIngredient,
    CacOrderSubContent
} from 'shared/types'
import { formatPrice, isDefined, orderPickUpType } from 'shared/utils'
import {
    getMenuNameFromSize,
    sumProductFinalPrices
} from 'shared/utils/ecommerce'
import { Pin } from 'app/assets/icons'
import { OpeningStatus } from 'app/common/restaurant'
import { H4, H6, SmallText } from 'app/common/typography'
import { fonts } from 'app/common/typography'

const { DELIVERY_IN_APP } = orderPickUpType

type OrderRecapProps = {
    order: EcommOrder
}
export const OrderRecap = ({ order }: OrderRecapProps) => {
    const {
        content,
        finalPrice = 0,
        originalPrice = 0,
        pickUpType,
        promotions,
        restaurant
    } = order
    const intl = useIntl()
    //$FlowFixMe
    const { deliveryFees } = order
    const isDelivery = pickUpType === DELIVERY_IN_APP
    const offeredDeliveryFees = deliveryFees?.length === 0
    const deliveryFeesPrice =
        (deliveryFees?.length > 0 && deliveryFees[0]?.finalPrice) || 0

    const purchasables = ecommOrderToCart(order)
    const orderPrice = purchasables
        ? sumProductFinalPrices(purchasables)
        : originalPrice

    const renderRestaurant = () => {
        const {
            id,
            name,
            cacStatus,
            openingCacHour,
            closingCacHour,
            addressFull
        } = restaurant || {}
        return (
            <StyledRestaurantContainer>
                <Pin />
                <StyledRestaurantInformations>
                    <StyledRestaurantName>{name}</StyledRestaurantName>
                    <StyledRestaurantAddress>
                        {addressFull}
                    </StyledRestaurantAddress>
                    {cacStatus && (
                        <StyledOpeningStatusContainer>
                            <OpeningStatus
                                id={id}
                                status={cacStatus}
                                openingHour={openingCacHour}
                                closingHour={closingCacHour}
                                fromEcommerce
                            />
                        </StyledOpeningStatusContainer>
                    )}
                </StyledRestaurantInformations>
            </StyledRestaurantContainer>
        )
    }

    const renderCustomizedIngredients = (
        ingredients: Array<CacOrderContentIngredient>
    ) => {
        return ingredients.map(
            (ingredient: CacOrderContentIngredient, index) => {
                const { label, initialQuantity, quantity, price } = ingredient
                if (!isDefined(quantity) || !isDefined(initialQuantity)) {
                    return null
                }
                const text =
                    //$FlowFixMe
                    quantity > initialQuantity
                        ? 'clickandcollect.cart.custo.extra'
                        : 'clickandcollect.cart.custo.without'
                const currentQty =
                    //$FlowFixMe
                    quantity - initialQuantity > 0
                        ? //$FlowFixMe
                          quantity - initialQuantity
                        : quantity
                return (
                    <div>
                        <SmallText>
                            {intl.formatMessage(
                                {
                                    id: text
                                },
                                { product: label, qty: currentQty || 1 }
                            )}

                            {!!price &&
                                //$FlowFixMe
                                (quantity > initialQuantity && price > 0
                                    ? ' (+' + formatPrice(price) + ')'
                                    : ' (' + formatPrice(price) + ')')}
                        </SmallText>
                    </div>
                )
            }
        )
    }

    const renderOptions = (freeItems: Array<CacOrderContent>) =>
        freeItems.map((freeItem, index) => {
            const { label, quantity } = freeItem
            return (
                <SmallText key={index}>
                    {!!quantity && quantity > 1 && quantity + 'x'} {label}
                </SmallText>
            )
        })

    const renderIceChoice = (noIce?: boolean) => (
        <FormattedMessage
            id={
                noIce
                    ? 'clickandcollect.catalog.drink.no.ice'
                    : 'clickandcollect.catalog.drink.with.ice'
            }>
            {text => <SmallText>{text}</SmallText>}
        </FormattedMessage>
    )

    const renderPickUpLaterChoice = (pickUpLater?: boolean) => (
        <FormattedMessage
            id={
                pickUpLater
                    ? 'ecommerce.catalog.pick.up.later'
                    : 'ecommerce.catalog.pick.up.now'
            }>
            {text => <SmallText>{text}</SmallText>}
        </FormattedMessage>
    )

    const renderSubContent = (
        subContent: CacOrderSubContent,
        size?: string,
        index: number
    ) => {
        const {
            label,
            quantity,
            freeItems,
            recipe,
            noIce,
            pickUpLater
        } = subContent
        const sizeMessageId = getMenuNameFromSize(size)

        return (
            <Fragment key={index}>
                <StyledSubContent>
                    {quantity && quantity > 1 && (
                        <StyledQuantity>{quantity}x</StyledQuantity>
                    )}
                    <SmallText>{label}</SmallText>
                    {index === 0 && (
                        <StyledSize>
                            <FormattedMessage id={sizeMessageId} />
                        </StyledSize>
                    )}
                </StyledSubContent>
                {!!freeItems && renderOptions(freeItems)}
                {!!recipe && renderCustomizedIngredients(recipe)}
                {isDefined(noIce) && renderIceChoice(noIce)}
                {isDefined(pickUpLater) && renderPickUpLaterChoice(pickUpLater)}
            </Fragment>
        )
    }

    const renderContent = (orderContent: CacOrderContent, index: number) => {
        const {
            label,
            finalPrice: finalItemPrice = 0,
            subContent,
            noIce,
            pickUpLater,
            freeItems,
            quantity,
            recipe,
            menuSize
        } = orderContent

        return (
            <StyledItemContainer key={index}>
                <StyledItemMain>
                    <StyledItemHeader>
                        {quantity && (
                            <StyledItemQuantity>{quantity}X</StyledItemQuantity>
                        )}
                        <StyledH6>{label}</StyledH6>
                    </StyledItemHeader>
                    {isDefined(finalItemPrice) && (
                        <SmallText>
                            {formatPrice(
                                quantity
                                    ? quantity * finalItemPrice
                                    : finalItemPrice
                            )}
                        </SmallText>
                    )}
                </StyledItemMain>
                {subContent &&
                    subContent.length > 0 &&
                    subContent.map((content, index) =>
                        renderSubContent(content, menuSize, index)
                    )}
                {!!freeItems && renderOptions(freeItems)}
                {!!recipe && renderCustomizedIngredients(recipe)}
                {isDefined(noIce) && renderIceChoice(noIce)}
                {isDefined(pickUpLater) && renderPickUpLaterChoice(pickUpLater)}
            </StyledItemContainer>
        )
    }

    return (
        <div>
            <FormattedMessage id="ecommerce.order.recap">
                {(t: string) => <H4>{t}</H4>}
            </FormattedMessage>
            {restaurant && renderRestaurant()}
            <StyledLine />
            {content &&
                content.length > 0 &&
                content.map((orderContent, index) =>
                    renderContent(orderContent, index)
                )}
            <StyledLine />
            {isDefined(orderPrice) && (
                <StyledPriceContainer>
                    <FormattedMessage id="clickandcollect.cart.subtotal">
                        {(text: string) => <SmallText>{text}</SmallText>}
                    </FormattedMessage>
                    <SmallText>{formatPrice(orderPrice)}</SmallText>
                </StyledPriceContainer>
            )}
            {promotions &&
                promotions.length > 0 &&
                promotions.map((promotion, index) => (
                    <Fragment key={index}>
                        <StyledPriceContainer>
                            <SmallText>{promotion.promotionName}</SmallText>
                            <SmallText>
                                {formatPrice(finalPrice - originalPrice)}
                            </SmallText>
                        </StyledPriceContainer>
                    </Fragment>
                ))}
            {isDelivery && (
                <StyledPriceContainer>
                    <FormattedMessage id="ecommerce.delivery.fees">
                        {(text: string) => <SmallText>{text}</SmallText>}
                    </FormattedMessage>
                    <SmallText>
                        {offeredDeliveryFees
                            ? intl.formatMessage({
                                  id: 'ecommerce.free.delivery.fees'
                              })
                            : isDefined(deliveryFeesPrice) &&
                              formatPrice(deliveryFeesPrice)}
                    </SmallText>
                </StyledPriceContainer>
            )}
            <StyledLine />
            {isDefined(finalPrice) && (
                <StyledPriceContainer>
                    <FormattedMessage id="clickandcollect.cart.total">
                        {(text: string) => <H4>{text}</H4>}
                    </FormattedMessage>
                    <H4>{formatPrice(finalPrice)}</H4>
                </StyledPriceContainer>
            )}
        </div>
    )
}

const StyledH6 = styled(H6)`
    text-transform: uppercase;
`

const StyledRestaurantContainer = styled.div`
    display: flex;
    margin: ${SPACING.S} ${SPACING.XXXS};
    flex-direction: row;
`

const StyledRestaurantName = styled(H6)`
    font-family: ${fonts.roboto};
    font-weight: bold;
    margin-left: ${SPACING.XXXS};
`

const StyledRestaurantAddress = styled(SmallText)`
    margin-left: ${SPACING.XXXS};
`

const StyledRestaurantInformations = styled.div`
    flex-direction: column;
    width: 90%;
`

const StyledOpeningStatusContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin: ${SPACING.NONE} ${SPACING.XS} ${SPACING.NONE} ${SPACING.XXXS};
`

const StyledLine = styled.div`
    width: 100%;
    height: 1px;
    background: ${colors.lightGrey};
    margin: ${SPACING.XXS} ${SPACING.NONE};
`

const StyledSubContent = styled.div`
    flex-direction: row;
    margin: ${SPACING.XXXS} ${SPACING.NONE};
`

const StyledQuantity = styled(SmallText)`
    margin-right: ${SPACING.XXXS};
`

const StyledSize = styled(SmallText)`
    margin-left: ${SPACING.XXXS};
`

const StyledItemContainer = styled.div`
    margin: ${SPACING.XS} ${SPACING.NONE};
`

const StyledItemMain = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`

const StyledItemHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
`

const StyledItemQuantity = styled(H6)`
    margin-right: ${SPACING.XXXS};
`

const StyledPriceContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: ${SPACING.XXXS} ${SPACING.NONE};
`
